import { authPx } from './authScale'

/**
 * The auth pages' TITLE and FIELD-LABEL type, in one place.
 *
 * Spencer, 2026-09-23, comparing this sign-in page with vera.inc's /enter gate
 * side by side: "I like the way the enter one looks more… update the font size…
 * capitalize the field titles on the platform page… I also like the more bolded
 * sign in font." So these two specs are /enter's, carried over as measured:
 *
 *   title   30px / 700   (was 24px / 600 here). /enter's h1 measured 30px/700.
 *   label   12px / 700 / UPPERCASE / 0.14em tracking, secondary colour
 *           (was 14px / 500 sentence case in text.primary). /enter's label
 *           measured 12px/700 with 1.68px tracking, which is 0.14em at 12px.
 *
 * ⚠️ THE LABEL IS FOR THE HAND-BUILT AUTH FIELDS ONLY. Sign-up's fields render
 * their label through `@repo/ui`'s `Input` → `FieldLabel`, which every form in
 * the platform shares; restyling that would uppercase every label in the
 * product. `Input` has no per-call label style, so sign-up keeps FieldLabel
 * until that is decided on purpose.
 *
 * Every auth title reads this constant — sign-in, sign-up (via its
 * PAGE_TITLE_SX), password setup, email verification and both 2FA screens — so
 * the flow cannot drift back into two title sizes, which is the defect
 * SignUpPage's PAGE_TITLE_SX was originally written to fix.
 */
/* 30 -> 24px, weight kept (Spencer, 2026-09-23: "reduce the titles so they
   don't compete with the VERA logo"). Inside the glass card the 140px
   wordmark sits directly above the title; at 30px/700 the two read as rival
   headings. 24px keeps the title unmistakably a title while leaving the mark
   as the one thing at the top. vera.inc /enter's "Invitation required"
   moved to 24px with it. */
export const AUTH_TITLE_SX = {
  fontWeight: 700,
  fontSize: authPx(24),
  lineHeight: 1.25,
  color: 'text.primary',
} as const

/** Uppercase, so it names the field ("EMAIL", "PASSWORD") rather than
 *  instructing — an instruction in tracked capitals reads as shouting, and the
 *  placeholder already carries "Enter your email". */
export const AUTH_FIELD_LABEL_SX = {
  fontWeight: 700,
  fontSize: authPx(12),
  lineHeight: 1.5,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  /* ⚠️ DARK IS BRIGHTER THAN text.secondary, AND THAT IS THE HALO'S DOING.
     Option B (SignInBackground, 2026-09-23) circles three lobes round the
     logo, and the EMAIL label sits just under it: zinc-400 on a passing lobe
     measured 3.56:1 in the worst frame, under the 4.5 AA this 12px text
     needs. 80% of text.primary keeps it a step below the title while
     clearing that — see the measurement in the commit. Light is unchanged
     (worst frame 4.98 on text.secondary). */
  color: (theme: { palette: { mode: string; text: { primary: string; secondary: string } } }) =>
    theme.palette.mode === 'dark'
      ? `color-mix(in srgb, ${theme.palette.text.primary} 80%, transparent)`
      : // LIGHT darkens too (2026-09-23): the lobes now sit behind the form
        // and are meant to be SEEN in light as well ("I can't see the blobs
        // on light mode"). zinc-500 on a purple-multiplied ground fell to
        // 3.97:1; 84% of text.primary keeps the label a clear step under
        // the 30px/700 title while clearing AA on the lobes (72% measured 3.68 on
        // the deep lobe; 84% below).
        `color-mix(in srgb, ${theme.palette.text.primary} 84%, transparent)`,
} as const
